const swiperClass = 'content-swiper';
const disabledClass = 'disabled';

/**
 * @typedef {HTMLDivElement & {swiper: Swiper}} SwiperContainerElement
 */

/**
 * inits the sliders
 * @return {Promise<void>}
 */
export async function initSliders() {
    /** @type {SwiperContainerElement[]} */
    const swipers = [...document.querySelectorAll(`.${swiperClass}`)];

    if (!swipers.length) {
        return;
    }

    const initPromises = swipers.map(element => {
        const config = element.dataset;

        if (config.responsiveInit > 0) {
            window.addEventListener('resize', () => {
                checkWidth(element, config);
            });
            return checkWidth(element, config);
        } else {
            return init(element, config);
        }
    });

    await Promise.all(initPromises);

    window.dispatchEvent(new CustomEvent('c3.swiper.global.initialized', {
        detail: {
            elements: swipers
        }
    }));
}

/**
 * @param {SwiperContainerElement} el
 * @param {SliderConfig | DOMStringMap} config
 */
async function checkWidth(el, config) {
    if (window.innerWidth < config.responsiveInit) {
        if (!el.swiper) {
            await init(el, config);
            el.classList.remove(disabledClass);
        }
    } else {
        el.classList.add(disabledClass);
        el.swiper?.destroy();
    }
}

/**
 * @param {SwiperContainerElement} el
 * @param {SliderConfig | DOMStringMap} config
 */
async function init(el, config) {
    const {Swiper} = await import('$c3base/swiper-imports.js');

    const swiperConfig = {
        slidesPerView: parseInt(config.slidesPerView ?? '1'),
        spaceBetween: parseInt(config.spaceBetween ?? '0'),
        grabCursor: config.grabCursor === 'true',
        centeredSlides: config.centeredSlides === 'true',
        effect: config.effect ?? 'slide',
        speed: parseInt(config.speed ?? '1000'),
        direction: config.direction,
        loop: config.loop === 'true',
        reverseDirection: config.reverseDirection === 'true',
        freeMode: config.freeMode === 'true',
        breakpoints: JSON.parse(config.breakpoints ?? '{}'),
        a11y: {
            firstSlideMessage: config.firstSlideMessage,
            lastSlideMessage: config.lastSlideMessage,
            nextSlideMessage: config.nextSlideMessage,
            paginationBulletMessage: config.paginationBulletMessage,
            prevSlideMessage: config.prevSlideMessage
        },
        modules: []
    };

    const pagination = el.querySelector('.swiper-pagination');
    if (pagination) {
        swiperConfig.pagination = {
            el: pagination,
            clickable: true
        };
    }

    const nextButton = el.parentElement.querySelector('.swiper-button-next');
    const prevButton = el.parentElement.querySelector('.swiper-button-prev');
    if (nextButton && prevButton) {
        swiperConfig.navigation = {
            nextEl: nextButton,
            prevEl: prevButton,
        };
    }

    const delay = parseInt(config.delay ?? '0');
    if (delay) {
        swiperConfig.autoplay = {
            delay,
        };
    }

    if (config.showOverflow === 'true') {
        const wrapper = document.createElement('div');
        wrapper.classList.add('swiper-overflow-wrap');
        el.parentElement.appendChild(wrapper);
        wrapper.append(el);
        el.style.maxWidth = config.width;
    }

    if (config.direction === 'vertical') {
        let maxHeight = 0;
        /** @type {HTMLDivElement[]} */
        const slides = [...el.querySelectorAll('.swiper-slide')];
        slides.forEach(slide => {
            if (maxHeight < slide.clientHeight) {
                maxHeight = slide.clientHeight;
            }
        });
        el.querySelector('.swiper-wrapper').style.height = `${maxHeight}px`;
    }

    if (config.thumbs === 'true') {
        const {Thumb} = await import('../Lib/swiper/swiper.js');

        swiperConfig.modules.push(Thumb);

        const thumbsFreeMode = config.thumbsFreeMode === 'true';
        const thumbSwiperConfig = {
            slidesPerView: parseInt(config.thumbsSlidesPerView ?? '1'),
            freeMode: thumbsFreeMode,
        };

        const thumbSwiper = new Swiper(el.parentElement.querySelector('.thumb-slider'), thumbSwiperConfig);
        swiperConfig.thumbs = {
            swiper: thumbSwiper
        };
    }

    switch (config.effect) {
        case 'flip':
            swiperConfig.slidesPerView = 1;
    }

    const swiper = new Swiper(el, swiperConfig);

    el.dispatchEvent(new CustomEvent('c3.swiper.initialized', {
        detail: {
            container: el,
            swiper
        }
    }));
}